import { next } from '@ember/runloop';

import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type ReportRecord from 'webapp/models/report';

export default class ReportSerializer extends ApplicationSerializer {
	override getReadOnlyFields(_json: Record<string, unknown>): RecordData.Report.ReadOnlyKey[] {
		return ['created', 'createdBy', 'newBadge'];
	}

	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.Report.Raw | RecordData.Report.Serialized,
		prop: string | undefined,
	) {
		const { settings, ...data } = json;
		const attributes: Partial<RecordAttributes<ReportRecord>> & { id?: string | number } = {
			...data,
		};

		if (settings) {
			if (typeof settings.restrictIps !== 'undefined') {
				attributes.restrictIps = settings.restrictIps;
			}
			if (typeof settings.password !== 'undefined') {
				attributes.password = settings.password;
			}
			if (typeof settings.allowAnonymousDateRangeEdit !== 'undefined') {
				attributes.allowAnonymousDateRangeEdit = settings.allowAnonymousDateRangeEdit;
			}
			if (typeof settings.allowAnonymousFilterEdit !== 'undefined') {
				attributes.allowAnonymousFilterEdit = settings.allowAnonymousFilterEdit;
			}
			if (typeof settings.cover !== 'undefined') {
				attributes.cover = settings.cover;
			}
			if (typeof settings.coverOriginal !== 'undefined') {
				attributes.coverOriginal = settings.coverOriginal;
			}
			if (typeof settings.coverCrop !== 'undefined') {
				attributes.coverCrop = settings.coverCrop;
			}
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.Report.Serialized {
		// read-only fields are already removed by ApplicationSerializer
		const attributes = super.serialize(snapshot, options) as Omit<
			RecordAttributes<ReportRecord>,
			RecordData.Report.ReadOnlyKey
		>;

		const settings: RecordData.Report.Settings = {};
		if (attributes.restrictIps) {
			settings.restrictIps = attributes.restrictIps;
		}
		if (attributes.password) {
			settings.password = attributes.password;
		}
		if (typeof attributes.allowAnonymousDateRangeEdit !== 'undefined') {
			settings.allowAnonymousDateRangeEdit = attributes.allowAnonymousDateRangeEdit;
		}
		if (typeof attributes.allowAnonymousFilterEdit !== 'undefined') {
			settings.allowAnonymousFilterEdit = attributes.allowAnonymousFilterEdit;
		}
		if (attributes.cover) {
			settings.cover = attributes.cover;
		}
		if (attributes.coverOriginal) {
			settings.coverOriginal = attributes.coverOriginal;
		}
		if (attributes.coverCrop) {
			settings.coverCrop = attributes.coverCrop;
		}

		const json: RecordData.Report.Serialized = {
			...omit(
				attributes,
				// moved to settings
				'restrictIps',
				'password',
				'allowAnonymousDateRangeEdit',
				'allowAnonymousFilterEdit',
				'cover',
				'coverOriginal',
				'coverCrop',
				// not sent
				'verificationInfo',
				'isSample',
			),
			settings,
		};

		// isSample is a transient flag - sent to BE once, then reset
		// Uses next() to defer the reset so it doesn't run before the actual save
		// (request-queue calls model.serialize() for diff comparison before model.save())
		if (attributes.isSample) {
			json.isSample = true;
			next(() => {
				(snapshot.record as ReportRecord).isSample = false;
			});
		}

		return json;
	}
}
